import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Loader2, Upload, Image as ImageIcon, Search } from 'lucide-react';
import LeagueIconPicker from './LeagueIconPicker';
import { getApiBaseUrl } from '../../utils/apiUrl';

const LeagueFormModal = ({ isOpen, onClose, league, onSuccess, showNotification }) => {
  const [formData, setFormData] = useState({
    name: '',
    name_th: '',
    cp_limit: 1500,
    has_cp_limit: true,
    description: '',
    description_th: '',
    icon: '',
    sort_order: 0,
    start_date: '',
    end_date: '',
    is_permanent: true,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [showIconPicker, setShowIconPicker] = useState(false);

  useEffect(() => {
    if (league) {
      setFormData({
        name: league.name || '',
        name_th: league.name_th || '',
        cp_limit: league.cp_limit || 1500,
        has_cp_limit: league.has_cp_limit !== undefined ? league.has_cp_limit : (league.cp_limit > 0),
        description: league.description || '',
        description_th: league.description_th || '',
        icon: league.icon || '',
        sort_order: league.sort_order || 0,
        start_date: league.start_date || '',
        end_date: league.end_date || '',
        is_permanent: league.is_permanent !== undefined ? league.is_permanent : true,
        is_active: league.is_active !== undefined ? league.is_active : true
      });
      if (league.icon) {
        setIconPreview(`/images/leagues/${league.icon}`);
      }
    } else {
      // Reset form for new league
      setFormData({
        name: '',
        name_th: '',
        cp_limit: 1500,
        has_cp_limit: true,
        description: '',
        description_th: '',
        icon: '',
        sort_order: 0,
        start_date: '',
        end_date: '',
        is_permanent: true,
        is_active: true
      });
      setIconPreview(null);
    }
    setError(null);
  }, [league, isOpen]);

  // Auto-detect icon when name changes
  useEffect(() => {
    if (formData.name && !formData.icon) {
      const autoDetected = autoDetectIcon(formData.name);
      if (autoDetected) {
        setFormData(prev => ({ ...prev, icon: autoDetected }));
        setIconPreview(`/images/leagues/${autoDetected}`);
      }
    }
  }, [formData.name]);

  // Auto-detect icon helper
  const autoDetectIcon = (leagueName) => {
    if (!leagueName) return null;
    const name = leagueName.toLowerCase();
    if (name.includes('great')) return 'great.webp';
    if (name.includes('ultra')) return 'ultra.webp';
    if (name.includes('master')) return 'master.webp';
    if (name.includes('little')) return 'little.webp';
    if (name.includes('retro')) return 'GBL_retrocup.webp';
    if (name.includes('premier')) return 'master_premier.webp';
    return null;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));
  };

  // Removed handleIconChange - now using icon picker

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('กรุณาเข้าสู่ระบบก่อน');
      }

      // Validation
      if (!formData.name || !formData.name_th) {
        throw new Error('กรุณากรอกชื่อลีกทั้งภาษาอังกฤษและภาษาไทย');
      }

      if (formData.has_cp_limit && (!formData.cp_limit || formData.cp_limit < 0)) {
        throw new Error('กรุณากรอก CP Limit ที่ถูกต้อง');
      }

      if (!formData.is_permanent) {
        if (!formData.start_date || !formData.end_date) {
          throw new Error('กรุณากรอกวันที่เริ่มต้นและสิ้นสุดสำหรับลีกชั่วคราว');
        }
        if (new Date(formData.start_date) >= new Date(formData.end_date)) {
          throw new Error('วันที่สิ้นสุดต้องมากกว่าวันที่เริ่มต้น');
        }
      }

      const url = league 
        ? `${getApiBaseUrl()}/api/leagues/${league.id}`
        : `${getApiBaseUrl()}/api/leagues`;
      
      const method = league ? 'PUT' : 'POST';

      // Prepare form data
      const submitData = {
        ...formData,
        cp_limit: formData.has_cp_limit ? formData.cp_limit : 0
      };

      // Icon is already set from picker or auto-detect
      // No need to handle iconFile anymore

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'เกิดข้อผิดพลาดในการบันทึก');
      }

      showNotification(league ? 'แก้ไขลีกสำเร็จ' : 'เพิ่มลีกสำเร็จ', 'success');
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
      showNotification(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <AnimatePresence>
        {/* Backdrop */}
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          key="modal"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {league ? 'แก้ไขลีก' : 'เพิ่มลีกใหม่'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
              </div>
            )}

            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">ข้อมูลพื้นฐาน</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ชื่อลีก (ภาษาอังกฤษ) *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="Great League"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ชื่อลีก (ภาษาไทย) *
                  </label>
                  <input
                    type="text"
                    name="name_th"
                    value={formData.name_th}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="ลีกใหญ่"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    คำอธิบาย (ภาษาอังกฤษ)
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="The most accessible league with a 1500 CP limit"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    คำอธิบาย (ภาษาไทย)
                  </label>
                  <textarea
                    name="description_th"
                    value={formData.description_th}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="ลีกที่เข้าถึงได้ง่ายที่สุดด้วยขีดจำกัด CP 1500"
                  />
                </div>
              </div>
            </div>

            {/* League Rules */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">กฎกติกาลีก</h3>
              
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="has_cp_limit"
                  checked={formData.has_cp_limit}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  มีขีดจำกัด CP
                </label>
              </div>

              {formData.has_cp_limit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    CP Limit *
                  </label>
                  <input
                    type="number"
                    name="cp_limit"
                    value={formData.cp_limit}
                    onChange={handleInputChange}
                    min="0"
                    max="10000"
                    required={formData.has_cp_limit}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="1500"
                  />
                  <p className="mt-1 text-xs text-gray-500">0 = ไม่มีขีดจำกัด (สำหรับ Master League)</p>
                </div>
              )}
            </div>

            {/* Icon Selection */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">ไอคอนลีก</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  เลือกไอคอนจากไลบรารี
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowIconPicker(true)}
                    className="flex items-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 cursor-pointer transition-colors"
                  >
                    <Search className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {formData.icon ? formData.icon.replace('.webp', '') : 'เลือกไอคอน'}
                    </span>
                  </button>
                  {iconPreview && (
                    <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                      <img
                        src={iconPreview}
                        alt="Icon preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
                {formData.name && !formData.icon && (
                  <p className="mt-2 text-xs text-gray-500">
                    💡 ระบบจะพยายามหาไอคอนอัตโนมัติจากชื่อลีก
                  </p>
                )}
              </div>
            </div>

            {/* Timing (for seasonal leagues) */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">ระยะเวลา</h3>
              
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="is_permanent"
                  checked={formData.is_permanent}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  ลีกถาวร (ไม่มีวันเริ่มต้นและสิ้นสุด)
                </label>
              </div>

              {!formData.is_permanent && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      วันที่เริ่มต้น
                    </label>
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      required={!formData.is_permanent}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      วันที่สิ้นสุด
                    </label>
                    <input
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleInputChange}
                      required={!formData.is_permanent}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">สถานะ</h3>
              
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  ใช้งานอยู่
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  ลำดับการแสดงผล
                </label>
                <input
                  type="number"
                  name="sort_order"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
                <p className="mt-1 text-xs text-gray-500">ตัวเลขน้อยกว่า = แสดงก่อน</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>กำลังบันทึก...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>บันทึก</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </AnimatePresence>

      {/* Icon Picker Modal */}
      {showIconPicker && (
        <LeagueIconPicker
          value={formData.icon}
          onChange={(icon) => {
            setFormData(prev => ({ ...prev, icon }));
            setIconPreview(`/images/leagues/${icon}`);
          }}
          onClose={() => setShowIconPicker(false)}
        />
      )}
    </div>
  );
};

export default LeagueFormModal;
